import logoRed from "../assets/images/logored.svg";

const COMPLIANCE_URL = "https://portal.strobeaviation.com/sms-compliance";
const TERMS_URL = "https://portal.strobeaviation.com/terms";
const PRIVACY_URL = "https://portal.strobeaviation.com/privacy";
const OPT_IN_SCREENSHOT_URL =
  "https://drive.google.com/file/d/136waNvKDv_ozbRnpJYrH9Hl6MirwcKuu/view?usp=sharing";

const brokerConsent =
  "Yes, I want to receive automated text message notifications from Strobe Aviation about my broker request submissions and project status updates. Message frequency varies based on account activity. Message and data rates may apply. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com. Consent is not required to use the portal.";

const adminConsent =
  "Yes, I want to receive automated text message notifications from Strobe Aviation about new broker requests, broker request edits, broker request deletes, and project status activity. Message frequency varies based on account activity. Message and data rates may apply. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com. Consent is not required to use the admin portal.";

const useCaseDescription =
  "Strobe Aviation sends transactional SMS notifications to opted-in broker users and internal admins about aircraft media/service requests in the Strobe Aviation portal. Messages include new request alerts, request edits, request deletes, scheduling updates, expected date changes, and project status updates. Recipients opt in from notification settings in the Strobe Aviation portal. No marketing or promotional content is sent.";

const sampleMessage =
  "Strobe Aviation: N123AB is now Scheduled for Jul 24, 2026 at 1:00 PM CT. Log in to the portal for details. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com.";

const optInConfirmation =
  "Strobe Aviation: You are now opted in to receive transactional SMS notifications about broker request submissions, request edits, scheduling updates, and project status updates. Message frequency varies based on account activity. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com.";

const helpResponse =
  "Strobe Aviation: For help, contact support@strobeaviation.com. Reply STOP to unsubscribe.";

const stopResponse =
  "Strobe Aviation: You have been unsubscribed from SMS notifications and will no longer receive text updates. For help, contact support@strobeaviation.com.";

const additionalInformation =
  "The Strobe Aviation portal requires login, so this public SMS compliance page is provided for review: https://portal.strobeaviation.com/sms-compliance. It includes business details, SMS use case, opt-in wording, screenshots of the portal opt-in flow, message frequency, STOP/HELP disclosures, Terms, and Privacy links. Messages are transactional operational notifications only and are not marketing, promotional, political, financial, health, or age-gated content. SMS opt-in data and consent are not sold, rented, shared, or transferred to third parties or affiliates.";

function InfoRow({ label, children }) {
  return (
    <div className="border-b border-black/10 py-4 last:border-b-0">
      <dt className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">{label}</dt>
      <dd className="mt-1 text-base text-black/80">{children}</dd>
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="border-t border-black/10 py-10">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#E60000]">{eyebrow}</p>
      <h2 className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function SampleMessage({ label, children }) {
  return (
    <div className="rounded-lg border border-black/10 bg-white p-5">
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">{label}</p>
      <p className="mt-3 text-base leading-7 text-black/75">{children}</p>
    </div>
  );
}

function SubmissionValue({ label, children }) {
  return (
    <div className="rounded-lg border border-black/10 bg-white p-5">
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">{label}</p>
      <p className="mt-3 whitespace-pre-line text-base leading-7 text-black/75">{children}</p>
    </div>
  );
}

export default function SmsCompliance() {
  return (
    <main className="min-h-screen bg-[#F6F7F9] px-5 py-6 text-[#1A1A1A] sm:px-8">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <a href="/" className="inline-flex items-center gap-3" aria-label="Strobe Aviation home">
            <img src={logoRed} alt="Strobe Aviation" className="h-9 w-auto" />
          </a>
          <nav className="flex flex-wrap gap-4 text-sm font-semibold text-black/65">
            <a className="underline decoration-[#E60000] underline-offset-4" href={TERMS_URL}>
              Terms
            </a>
            <a className="underline decoration-[#E60000] underline-offset-4" href={PRIVACY_URL}>
              Privacy
            </a>
            <a className="underline decoration-[#E60000] underline-offset-4" href="mailto:support@strobeaviation.com">
              Contact
            </a>
          </nav>
        </header>

        <article className="rounded-lg bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.08)] sm:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#E60000]">
              Public SMS Compliance Page
            </p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-black sm:text-5xl">
              Strobe Aviation SMS Notifications
            </h1>
            <p className="mt-5 text-lg leading-8 text-black/65">
              This public page explains Strobe Aviation's SMS notification program, consent flow,
              message content, and opt-out instructions. The Strobe Aviation portal requires login,
              so this page is provided as a public review page for toll-free SMS verification.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">Program Type</p>
              <p className="mt-2 text-lg font-semibold">Transactional</p>
            </div>
            <div className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">Opt-In Type</p>
              <p className="mt-2 text-lg font-semibold">Web Form</p>
            </div>
            <div className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-black/45">Toll-Free Number</p>
              <p className="mt-2 text-lg font-semibold">+1 888-878-7623</p>
            </div>
          </div>

          <Section eyebrow="Business Details" title="Strobe Aviation LLC">
            <dl className="rounded-lg border border-black/10 bg-[#FAFAFA] px-5">
              <InfoRow label="Business Name">Strobe Aviation LLC</InfoRow>
              <InfoRow label="Business Website">{COMPLIANCE_URL}</InfoRow>
              <InfoRow label="Support Email">
                <a className="text-[#E60000] underline underline-offset-4" href="mailto:support@strobeaviation.com">
                  support@strobeaviation.com
                </a>
              </InfoRow>
              <InfoRow label="Service Description">
                Strobe Aviation provides an authenticated broker and admin portal for managing aircraft
                media and aviation service requests, including request intake, scheduling, expected timing,
                project status updates, and operational coordination.
              </InfoRow>
            </dl>
          </Section>

          <Section eyebrow="SMS Use Case" title="What Recipients Receive">
            <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
              <div className="space-y-4 text-base leading-7 text-black/70">
                <p>{useCaseDescription}</p>
                <p className="font-semibold text-black">
                  Strobe Aviation does not send marketing, promotional, political, financial, health,
                  or age-gated content through this SMS program.
                </p>
              </div>
              <dl className="rounded-lg border border-black/10 bg-[#FAFAFA] px-5">
                <InfoRow label="Message Frequency">Varies based on account and project activity.</InfoRow>
                <InfoRow label="Rates">Message and data rates may apply.</InfoRow>
                <InfoRow label="Opt Out">Reply STOP to unsubscribe.</InfoRow>
                <InfoRow label="Help">For help, contact support@strobeaviation.com.</InfoRow>
              </dl>
            </div>
          </Section>

          <Section eyebrow="Twilio Submission Values" title="Exact Values Used For Verification">
            <div className="grid gap-5">
              <SubmissionValue label="Business Website URL">{COMPLIANCE_URL}</SubmissionValue>
              <SubmissionValue label="Estimated Monthly SMS Volume">100</SubmissionValue>
              <SubmissionValue label="Use Case">Account_notifications</SubmissionValue>
              <SubmissionValue label="Use Case Description">{useCaseDescription}</SubmissionValue>
              <SubmissionValue label="Sample Message">{sampleMessage}</SubmissionValue>
              <SubmissionValue label="Opt-In Type">Web Form</SubmissionValue>
              <SubmissionValue label="Opt-In Policy Proof">
                {`${COMPLIANCE_URL}\n${OPT_IN_SCREENSHOT_URL}`}
              </SubmissionValue>
              <SubmissionValue label="Terms And Conditions URL">{TERMS_URL}</SubmissionValue>
              <SubmissionValue label="Privacy Policy URL">{PRIVACY_URL}</SubmissionValue>
              <SubmissionValue label="Opt-In Keywords">Leave blank. Opt-in is collected by web form.</SubmissionValue>
              <SubmissionValue label="Opt-In Confirmation Message">{optInConfirmation}</SubmissionValue>
              <SubmissionValue label="Help Message">{helpResponse}</SubmissionValue>
              <SubmissionValue label="Age-Gated Content">No</SubmissionValue>
              <SubmissionValue label="Additional Information">{additionalInformation}</SubmissionValue>
              <SubmissionValue label="Notification Email">support@strobeaviation.com</SubmissionValue>
            </div>
          </Section>

          <Section eyebrow="Consent Flow" title="How Users Opt In">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <ol className="space-y-4 text-base leading-7 text-black/70">
                <li className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
                  <span className="font-bold text-black">1. Login required for portal users.</span>
                  <br />
                  Broker users and admins access notification preferences after signing in to the portal.
                </li>
                <li className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
                  <span className="font-bold text-black">2. SMS is separate from email.</span>
                  <br />
                  The Mobile phone (SMS) checkbox is separate from Email notifications.
                </li>
                <li className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
                  <span className="font-bold text-black">3. SMS is not preselected.</span>
                  <br />
                  Users must actively check Mobile phone (SMS), enter a mobile number, and save preferences.
                </li>
                <li className="rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
                  <span className="font-bold text-black">4. Consent details are shown before saving.</span>
                  <br />
                  The form states what messages are sent, message frequency, rates, STOP, HELP, and that
                  consent is not required to use the portal.
                </li>
              </ol>

              <div className="rounded-lg border border-black/10 bg-white p-4 shadow-[0_10px_30px_rgba(0,0,0,0.08)]">
                <div className="border-b border-black/10 pb-4">
                  <h3 className="text-xl font-bold">Notification Preferences</h3>
                  <p className="mt-1 text-sm text-black/60">
                    Choose how Strobe Aviation should notify you when your broker requests change status.
                  </p>
                </div>
                <div className="grid gap-4 py-5 sm:grid-cols-2">
                  <label className="flex items-center gap-3 text-base">
                    <span className="block h-5 w-5 rounded border border-black/40 bg-white" aria-hidden="true" />
                    Email notifications
                  </label>
                  <label className="flex items-center gap-3 text-base">
                    <span className="block h-5 w-5 rounded border border-black/40 bg-white" aria-hidden="true" />
                    Mobile phone (SMS)
                  </label>
                  <div className="rounded-lg border border-black/10 px-4 py-3 text-black/45">you@company.com</div>
                  <div className="rounded-lg border border-black/10 px-4 py-3 text-black/45">+1 555 123 4567</div>
                </div>
                <div className="rounded-lg border border-black/10">
                  <div className="border-b border-black/10 px-5 py-3 font-semibold">SMS consent details</div>
                  <div className="space-y-4 px-5 py-4 text-sm leading-6 text-black/70">
                    <p>{brokerConsent}</p>
                    <p>
                      Message frequency: varies based on account activity. Standard rates: message and data
                      rates may apply.
                    </p>
                    <p>
                      <a className="text-[#E60000] underline underline-offset-4" href={TERMS_URL}>
                        Terms of Service
                      </a>
                      <span> | </span>
                      <a className="text-[#E60000] underline underline-offset-4" href={PRIVACY_URL}>
                        Privacy Policy
                      </a>
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  <span className="rounded-lg border border-black/10 px-5 py-3 text-black/70">Cancel</span>
                  <span className="rounded-lg bg-[#E60000] px-5 py-3 font-semibold text-white">
                    Enable Notifications
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-black/10 bg-[#FAFAFA] p-5">
              <p className="text-sm font-semibold text-black">Public opt-in screenshot</p>
              <p className="mt-2 text-sm leading-6 text-black/65">
                A public screenshot of the portal opt-in modal is available at{" "}
                <a
                  className="text-[#E60000] underline underline-offset-4"
                  href={OPT_IN_SCREENSHOT_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  this Google Drive proof URL
                </a>
                . The screenshot is provided because the actual portal is authenticated.
              </p>
            </div>
          </Section>

          <Section eyebrow="Exact Consent Language" title="SMS Consent Text Shown In The Portal">
            <div className="grid gap-5">
              <SampleMessage label="Broker User SMS Consent">{brokerConsent}</SampleMessage>
              <SampleMessage label="Admin User SMS Consent">{adminConsent}</SampleMessage>
            </div>
          </Section>

          <Section eyebrow="Message Examples" title="Sample SMS Messages">
            <div className="grid gap-5">
              <SampleMessage label="Scheduling Update">
                {sampleMessage}
              </SampleMessage>
              <SampleMessage label="New Request Alert">
                Strobe Aviation: New broker request submitted for N123AB. Status: Requested. Log in to the
                portal for details. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com.
              </SampleMessage>
              <SampleMessage label="Project Status Update">
                Strobe Aviation: N123AB project status changed to Production. Log in to the portal for
                details. Reply STOP to unsubscribe. For help, contact support@strobeaviation.com.
              </SampleMessage>
            </div>
          </Section>

          <Section eyebrow="STOP And HELP" title="Opt-Out And Support Responses">
            <div className="grid gap-5 md:grid-cols-2">
              <SampleMessage label="HELP Response">
                {helpResponse}
              </SampleMessage>
              <SampleMessage label="STOP Response">
                {stopResponse}
              </SampleMessage>
            </div>
          </Section>

          <Section eyebrow="Data Protection" title="SMS Consent Is Not Shared">
            <div className="space-y-4 text-base leading-7 text-black/70">
              <p>
                SMS opt-in data and consent are not sold, rented, shared, or transferred to third parties
                or affiliates. SMS consent is used only to send the transactional notifications selected by
                the opted-in portal user.
              </p>
              <p>
                Strobe Aviation uses Twilio only as a messaging service provider to deliver SMS messages to
                users who explicitly opt in. Strobe Aviation does not use SMS opt-in information for
                unrelated marketing campaigns.
              </p>
              <p>
                Additional notification terms are available in the{" "}
                <a className="text-[#E60000] underline underline-offset-4" href={TERMS_URL}>
                  Terms of Service
                </a>{" "}
                and{" "}
                <a className="text-[#E60000] underline underline-offset-4" href={PRIVACY_URL}>
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </Section>
        </article>
      </div>
    </main>
  );
}
